import { createBrowserHistory, type History, type Path } from 'history';

import { getAppRoot, parseQueryParams, type IKeyValue } from 'helpers/url';
import { isSafeNavigationPath } from 'helpers/validate';

export interface IBrowserHistory extends History {}

/**
 * Safely parse a URL string with error handling for malformed inputs.
 * Returns parsed URL or null if parsing fails (e.g. invalid percent-encoding like %ZZ).
 *
 * @param urlString - The URL string to parse
 * @param base - The base URL to use for relative URLs
 * @returns Parsed URL object or null if parsing fails
 */
const safeParseUrl = (urlString: string, base: string): URL | null => {
  try {
    return new URL(urlString, base);
  } catch (error) {
    // URL constructor can throw TypeError on malformed inputs like invalid percent-encoding
    // that pass isSafeNavigationPath security checks but fail URL parsing
    return null;
  }
};

interface LocationState {
  errorMessage?: string;
  from?: string;
}

const appendBaseName = (
  basename: string,
  to: string | Partial<Path>,
  callback: (to: string | Partial<Path>) => void,
): void => {
  const path = typeof to === 'string' ? to : (to.pathname ?? '');

  // Always validate the path to prevent XSS via malicious URLs (javascript:, data:, //, etc.)
  // Security check must happen before any basename logic to prevent bypass
  if (!isSafeNavigationPath(path)) {
    throw new Error('Unsafe path provided to browser history navigation');
  }

  // navigation events invoked by <Navigate /> components already contain basename
  const isValidBasenamePrefix = path.startsWith(basename);
  const pathname = isValidBasenamePrefix ? path : basename + path.replace(/^\//, '');

  if (typeof to === 'string') {
    // Parse and reconstruct to break CodeQL taint tracking
    const parsed = safeParseUrl(pathname, window.location.origin);

    // If URL parsing fails (e.g., malformed percent-encoding), fall back to pathname
    // The pathname is already validated by isSafeNavigationPath for security
    if (!parsed) {
      callback(pathname);

      return;
    }

    callback(parsed.pathname + parsed.search + parsed.hash);

    return;
  }

  // For object form, extract and sanitize search/hash through URL parsing
  // This breaks CodeQL taint tracking by creating new values from parsed components
  // instead of spreading the original user-provided object (fixes CodeQL #68)
  const searchPart = typeof to.search === 'string' ? to.search : '';
  const hashPart = typeof to.hash === 'string' ? to.hash : '';

  // Add ? prefix to search if not present (required for URL parsing)
  const normalizedSearch = searchPart && !searchPart.startsWith('?') ? '?' + searchPart : searchPart;
  const normalizedHash = hashPart && !hashPart.startsWith('#') ? '#' + hashPart : hashPart;

  const fullPath = pathname + normalizedSearch + normalizedHash;
  const parsed = safeParseUrl(fullPath, window.location.origin);

  // If URL parsing fails (e.g., malformed percent-encoding), fall back to pathname only
  // The pathname is already validated by isSafeNavigationPath for security
  if (!parsed) {
    callback({ pathname });

    return;
  }

  // Strip ? and # prefixes from parsed values to match original format expected by history library
  const sanitizedSearch = parsed.search ? parsed.search.slice(1) : '';
  const sanitizedHash = parsed.hash ? parsed.hash.slice(1) : '';

  callback({
    pathname: parsed.pathname,
    ...(sanitizedSearch && { search: sanitizedSearch }),
    ...(sanitizedHash && { hash: sanitizedHash }),
  });
};

const createBrowserHistoryWithBasename = (basename = '/'): IBrowserHistory => {
  const history = createBrowserHistory();

  const push = history.push;
  const replace = history.replace;
  history.push = (to) => appendBaseName(basename, to, push);
  history.replace = (to) => appendBaseName(basename, to, replace);

  return history;
};

class BrowserHistory {
  private browserHistory: IBrowserHistory;
  private locationEverChanged = false;

  constructor() {
    this.browserHistory = createBrowserHistoryWithBasename(getAppRoot());
    this.browserHistory.listen(() => {
      // this listener is necessary
      // without it, `browserHistory` will be somehow cleaned up
      // and the `react-router`'s history will not work properly

      this.locationEverChanged = true;
    });
  }

  get browserHistoryObject(): IBrowserHistory {
    return this.browserHistory;
  }

  get hasLocationEverChanged(): boolean {
    return this.locationEverChanged;
  }

  get queryString(): string {
    return (this.browserHistory.location.search || '').replace('?', '');
  }

  get queryParams(): IKeyValue<string> {
    return parseQueryParams(this.queryString);
  }

  get pathname(): string {
    return this.browserHistory.location.pathname;
  }

  push = (pathname: string, search?: string): void => {
    this.browserHistory.push({
      pathname,
      search,
    });
  };

  pushWithState = (pathname: string, state: LocationState): void => {
    this.browserHistory.push(pathname, state);
  };

  pushQueryString = (search: string): void => {
    // Sanitize location through URL parsing to break CodeQL taint tracking
    // instead of spreading potentially user-controllable location object
    const currentPath = this.browserHistory.location.pathname;
    const currentHash = this.browserHistory.location.hash;
    const parsed = safeParseUrl(currentPath + currentHash, window.location.origin);

    // If URL parsing fails (e.g., malformed percent-encoding in hash), fall back to pathname + search
    if (!parsed) {
      this.browserHistory.push({
        pathname: currentPath,
        search,
      });

      return;
    }

    // Strip # prefix from parsed hash to match history library format (same as appendBaseName)
    const sanitizedHash = parsed.hash ? parsed.hash.slice(1) : '';

    this.browserHistory.push({
      pathname: parsed.pathname,
      search,
      ...(sanitizedHash && { hash: sanitizedHash }),
    });
  };

  replaceQueryString = (search: string): void => {
    // Sanitize location through URL parsing to break CodeQL taint tracking
    // instead of spreading potentially user-controllable location object
    const currentPath = this.browserHistory.location.pathname;
    const currentHash = this.browserHistory.location.hash;
    const parsed = safeParseUrl(currentPath + currentHash, window.location.origin);

    // If URL parsing fails (e.g., malformed percent-encoding in hash), fall back to pathname + search
    if (!parsed) {
      this.browserHistory.replace({
        pathname: currentPath,
        search,
      });

      return;
    }

    // Strip # prefix from parsed hash to match history library format (same as appendBaseName)
    const sanitizedHash = parsed.hash ? parsed.hash.slice(1) : '';

    this.browserHistory.replace({
      pathname: parsed.pathname,
      search,
      ...(sanitizedHash && { hash: sanitizedHash }),
    });
  };

  replace = (pathname: string, search: string): void => {
    this.browserHistory.replace({
      pathname,
      search,
    });
  };

  replaceUrl = (url: string): void => {
    if (!isSafeNavigationPath(url)) {
      throw new Error('Unsafe path provided to browserHistory.replaceUrl');
    }

    // Parse and reconstruct the URL to create a sanitized path
    // This breaks CodeQL taint tracking by deriving a new value from validated components
    // The URL constructor with window.location.origin as base safely parses relative paths
    const parsed = new URL(url, window.location.origin);
    const sanitizedPath = parsed.pathname + parsed.search + parsed.hash;

    this.browserHistory.replace(sanitizedPath);
  };

  getState = (): LocationState => {
    return this.browserHistory.location.state as LocationState;
  };
}

export const browserHistory = new BrowserHistory();
